import { clsx } from 'clsx';
import type { CSSProperties } from 'react';
import type { LoadingIndicatorProps, LoadingIndicatorSize } from './LoadingIndicator.types';

const SPINNER_SIZE: Record<LoadingIndicatorSize, number> = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 40,
  xl: 64,
};

const DEFAULT_THICKNESS: Record<LoadingIndicatorSize, number> = {
  xs: 2,
  sm: 3,
  md: 4,
  lg: 7,
  xl: 10,
};

const toPixels = (value: LoadingIndicatorSize | number | undefined): string | undefined => {
  if (typeof value === 'number') {
    return `${value}px`;
  }

  return value ? `${SPINNER_SIZE[value]}px` : undefined;
};

const getDefaultThickness = (size: LoadingIndicatorSize | number | undefined): number | undefined => {
  if (typeof size === 'number') {
    return Math.max(2, Math.round(size / 6));
  }

  return size ? DEFAULT_THICKNESS[size] : undefined;
};

/**
 * Shared loading spinner that inherits the active Seeq theme color unless a color is provided.
 */
const LoadingIndicator = ({
  id,
  customId,
  testId,
  size,
  thickness,
  color,
  extraClassNames,
  style,
  ariaLabel,
  ariaHidden,
  role,
  onClick,
  title,
}: LoadingIndicatorProps) => {
  const isDecorative = !ariaLabel && !role && !onClick;
  const spinnerStyle = {
    ...style,
    ...(size ? { '--spinner-size': toPixels(size) } : {}),
    ...(thickness || size ? { '--thickness': `${thickness ?? getDefaultThickness(size)}px` } : {}),
    ...(color ? { '--spinner-color': color } : {}),
  } as CSSProperties;

  return (
    <div
      id={id}
      data-customid={customId}
      data-testid={testId}
      aria-hidden={ariaHidden ?? (isDecorative ? true : undefined)}
      aria-label={ariaLabel}
      role={role}
      title={title}
      className={clsx(
        // oxlint-disable-next-line better-tailwindcss/no-unknown-classes
        'loading-spinner',
        extraClassNames,
      )}
      style={spinnerStyle}
      onClick={onClick}
    />
  );
};

export default LoadingIndicator;
